import { createFileRoute } from "@tanstack/react-router";
import { localProductionSection } from "@/lib/seo/local-production";
import { LandingPage, type LandingFAQ } from "@/components/site/landing/LandingPage";
import { breadcrumbs, faqPage, localBusinessForCity, aeoGeo } from "@/lib/seo/schema";
import { karlstadTrust, karlstadProductionPricing, karlstadProcess } from "@/content/landing/karlstad";

const FAQ: LandingFAQ[] = [
  {
    "q": "Vad är skillnaden mellan filmproduktion och en filmbyrå?",
    "a": "Filmproduktion är hantverket. Filmbyrå inkluderar även strategi och kreativ idé. Fenny levererar båda i samma fasta pris."
  },
  {
    "q": "Hur ser en typisk filmproduktion i Karlstad ut?",
    "a": "Brief (v 1) → manus & storyboard (v 2) → pre-prod (v 3) → inspelning 1–3 dagar (v 3–4) → klipp, grade, ljudmix (v 4–6) → leverans."
  },
  {
    "q": "Vad kostar filmproduktion i Karlstad 2026?",
    "a": "Social-content: 40 000–120 000 kr. Reklamfilm 15–30 s: 150 000–450 000 kr. Brand-/industrifilm: 250 000–700 000 kr. Pulse: från 14 995 kr/mån."
  },
  {
    "q": "Hur snabbt kan ni vara igång?",
    "a": "Första möte inom 5 arbetsdagar. Pulse: första klipp inom 10–14 dagar."
  },
  {
    "q": "Var i Värmland filmar ni?",
    "a": "Hela regionen — Inre Hamn, KAU, Hammarö, Kristinehamn, Forshaga och Säffle samt Hammarö, Kristinehamn, Forshaga och hela Värmland. Drönar-tillstånd för aktuella zoner."
  },
  {
    "q": "Vilken utrustning använder ni?",
    "a": "Sony FX6/FX9 eller ARRI Alexa Mini. Aputure/ARRI-ljus. DJI-drönare. Post i DaVinci Resolve och Premiere."
  },
  {
    "q": "Klarar ni krävande industri- och säkerhetsmiljöer?",
    "a": "Ja. Vana vid pappers- och processindustri, bygg och KAU. PPE, NDA, säkerhetsbriefer och samordning med skiftledare."
  },
  {
    "q": "Tar ni hand om manus och regi?",
    "a": "Ja. Vana att översätta komplex verksamhet till säljande film."
  },
  {
    "q": "Hur många versioner ingår?",
    "a": "En master per format. Engelska versioner för export-/forskningskommunikation vid behov."
  },
  {
    "q": "Får vi raw-materialet?",
    "a": "Standard: mastrade filer + stillbilder. Raw lagras 12 månader."
  },
  {
    "q": "Kan vi vara med på set?",
    "a": "Ja. Live-feed via Frame.io för distans-kunder."
  },
  {
    "q": "Hur fungerar betalning?",
    "a": "50% vid produktionsstart, 50% vid leverans."
  }
];

export const Route = createFileRoute("/filmproduktion_/karlstad")({
  head: () => ({
    meta: [
      { title: "Filmproduktion Karlstad — fast pris, 4–6 v leverans | Fenny" },
      { name: "description", content: "Filmproduktion i Karlstad med hela kedjan inhouse. Reklam-, brand-, industri- och företagsfilm. Fast pris, 4–6 v. Vana vid pappers- och processindustri, bygg." },
      { property: "og:title", content: "Filmproduktion Karlstad — Fenny" },
      { property: "og:description", content: "Filmproduktion i Karlstad med fast pris och 4–6 v leverans." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/filmproduktion/karlstad" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/filmproduktion/karlstad" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/filmproduktion/karlstad", name: "Filmproduktion Karlstad — fast pris, 4–6 v leverans | Fenny", description: "Filmproduktion i Karlstad med hela kedjan inhouse. Reklam-, brand-, industri- och företagsfilm. Fast pris, 4–6 v. Vana vid pappers- och processindustri, bygg."})) },
      { type: "application/ld+json", children: JSON.stringify(localBusinessForCity({ city: "Karlstad", path: "/filmproduktion/karlstad", description: "Fenny — premium filmbyrå i Karlstad. Reklamfilm, brand och industrifilm för Värmland.", postalRegion: "Värmlands län" })) },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbs([{ name: "Hem", path: "/" }, { name: "Filmproduktion", path: "/filmproduktion" }, { name: "Karlstad", path: "/filmproduktion/karlstad" }])) },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <LandingPage
      eyebrow="Filmproduktion · Karlstad"
      title={<>Filmproduktion i Karlstad — utan månader av väntan.</>}
      lede="Fenny producerar film i Karlstad med fast pris och 4–6 veckors leverans. Reklamfilm, brand-, industri- och företagsfilm. Hela produktionskedjan inhouse. Djup vana vid pappers- och processindustri (Stora Enso, BillerudKorsnäs, Valmet), bygg, KAU och offentlig sektor."
      tldr={<p>Fenny är ett produktionsbolag i <strong>Karlstad</strong> med fullt team. Vi producerar film för varumärken inom pappers- och processindustri, bygg och KAU. <strong>Fast pris</strong>, 4–6 v.</p>}
      ctaPrimary={{ label: "Få offert inom 48 h", to: "/kontakt" }}
      ctaSecondary={{ label: "Se priser", to: "#priser" }}
      trackingId="filmproduktion-karlstad"
      trust={karlstadTrust}
      bullets={[
        { title: "Värmland-vana", body: "Djup erfarenhet av pappers- och processindustri, bygg och KAU. PPE och säkerhetsbriefer är rutin." },
        { title: "Snabb start", body: "Första möte inom 5 arbetsdagar. Offert inom 48 h." },
        { title: "Hela kedjan inhouse", body: "Strategi, manus, regi, foto, ljud, klipp, grade." },
      ]}
      pricing={{ heading: "Vad filmproduktion i Karlstad kostar", intro: "Riktpriser för 2026.", rows: karlstadProductionPricing, footnote: "Alla priser exkl. moms." }}
      process={{ heading: "Från brief till leverans på 4–6 veckor", intro: "Linjär process med skriftligt godkännande.", steps: karlstadProcess }}
      sections={[
        localProductionSection("karlstad", "Karlstad"),
        { id: "vem", heading: "Vem jobbar vi med i Karlstad?", body: <p>Pappers- och processindustri (Stora Enso, BillerudKorsnäs, Valmet), bygg, KAU och offentlig sektor samt tillväxtbolag i Hammarö, Kristinehamn, Forshaga och hela Värmland. Se <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/case">case</a>.</p> },
        { id: "tjanster", heading: "Vad producerar vi?", body: <p><a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/reklamfilm">Reklamfilm</a>, <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/foretagsfilm">företagsfilm</a>, <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/employer-branding-film">employer branding</a>, <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/produktfilm">produktfilm</a>, <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/instruktionsfilm">instruktionsfilm</a>, <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/3d-animation">3D-animation</a> och <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/dronarfilm">drönarfilm</a>.</p> },
        { id: "utrustning", heading: "Utrustning och teknik", body: <p>Sony FX6/FX9 eller ARRI Alexa Mini. Aputure/ARRI-ljus, DJI-drönare. Post i DaVinci Resolve och Premiere.</p> },
        { id: "andra-stader", heading: "Filmproduktion i andra städer", body: <p>Vi producerar även i <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/filmproduktion/stockholm">Stockholm</a>, <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/filmproduktion/goteborg">Göteborg</a>, <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/filmproduktion/malmo">Malmö</a> och <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/filmproduktion/orebro">Örebro</a>.</p> },
      ]}
      faq={FAQ}
      caseLimit={6}
      closing={{ title: "Möts vi i Karlstad?", body: "30 minuter på ert kontor eller över video.", primary: { label: "Boka strategimöte", to: "/kontakt" }, secondary: { label: "Räkna pris", to: "/priser" } }}
    />
  );
}
